import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, ArrowRight, Mail, Lock } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

/**
 * AdminPortalLogin — hidden, unadvertised admin-only login page.
 *
 * Lives at /admin-portal. Not linked from the public nav, footer or
 * the universal /login form. Posts ``expected_role: "admin"`` so the
 * backend returns a generic "Invalid credentials" for non-admin users
 * (no email-existence leak).
 *
 * This mirrors how LinkedIn / Indeed / Glassdoor keep staff auth on a
 * separate URL that the public never sees.
 */
export default function AdminPortalLogin() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const r = await login(email, password, "admin");
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    if (r.user.role !== "admin") {
      // Defense-in-depth: backend already enforces this with a 401, but
      // guard the client-side path too.
      setError("Invalid credentials");
      return;
    }
    nav("/admin/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <BrandLogo size={40} testId="admin-portal-brand" />
        </div>
        <div
          className="bg-white border rounded-3xl shadow-[0_20px_60px_-30px_rgba(0,40,168,0.35)] overflow-hidden"
          style={{ borderColor: "var(--rule)" }}
          data-testid="admin-portal-card"
        >
          <div
            className="px-6 sm:px-7 pt-6 sm:pt-7 pb-5"
            style={{
              background: "radial-gradient(120% 100% at 0% 0%, rgba(0,64,168,0.12) 0%, transparent 60%), white",
              borderBottom: "1px solid var(--rule)",
            }}
          >
            <div className="flex items-center gap-2.5">
              <div
                className="w-10 h-10 rounded-2xl grid place-items-center"
                style={{ background: "rgba(0,64,168,0.12)", color: "#0040A8" }}
              >
                <ShieldCheck size={20} strokeWidth={2.2} />
              </div>
              <span className="eyebrow" style={{ color: "#0040A8" }}>Staff access</span>
            </div>
            <h1 className="font-display font-black text-2xl sm:text-3xl mt-3 leading-tight">
              Admin portal
            </h1>
            <p className="text-sm text-muted mt-1.5">
              Restricted area. This portal is for authorised SkillConnect operators only.
            </p>
          </div>

          <div className="px-6 sm:px-7 pt-5 pb-7">
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="group">
                <label className="text-xs font-bold uppercase tracking-wider text-muted" htmlFor="admin-email">Email</label>
                <div className="relative mt-1.5">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-signal transition-colors pointer-events-none" />
                  <input
                    id="admin-email" type="email" required autoFocus autoComplete="email"
                    className="input input-with-icon"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="admin-portal-email"
                    placeholder="admin@skillconnect.com"
                  />
                </div>
              </div>
              <div className="group">
                <label className="text-xs font-bold uppercase tracking-wider text-muted" htmlFor="admin-password">Password</label>
                <div className="relative mt-1.5">
                  <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-signal transition-colors pointer-events-none" />
                  <input
                    id="admin-password" type="password" required autoComplete="current-password"
                    className="input input-with-icon"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="admin-portal-password"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              {error && (
                <div
                  className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2"
                  data-testid="admin-portal-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit" disabled={busy}
                className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-2xl font-bold text-sm text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-12px_rgba(0,64,168,0.55)] active:translate-y-0 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: "linear-gradient(135deg, #0040A8 0%, #0040A8cc 100%)" }}
                data-testid="admin-portal-submit"
              >
                {busy ? (
                  <>
                    <span className="inline-block w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Verifying…
                  </>
                ) : (
                  <>Enter <ArrowRight size={15} /></>
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-[11px] text-muted">
              All access attempts are logged. Unauthorized access is prohibited.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
